import { useEffect, useState } from "react";
import logo from "src/assets/img/logo.svg";
import { Link, useNavigate } from "react-router-dom";
import Email from "src/assets/icons/Email";
import Password from "src/assets/icons/Password";
import { PATH_AUTH, PATH_ROOT } from "src/routes/paths";
import { FormProvider, RHFTextField } from "src/components/Reusable/hook-form";
import { useLoginForm } from "src/hooks";
import EnvironmentSwitcher from "src/components/EnvironmentSwitcher";
import { isNonProdEnvironment } from "src/constants/api.constants";
import TextRing3D from "src/components/TextRing3D";
import loginImg from 'src/assets/img/login-background.webp'; // Default image for login
import { SpinnerIcon } from "src/assets/icons/Spinner";
import { Button } from "src/components/ui/Button";
import { Checkbox } from "src/components/ui/Checkbox";
import { cn } from "src/utils/Functions";
import { navigateToDemoEntry } from "@/views/demo-ari/welcome/useDemoWelcomeLaunch";

const Login = () => {
  const navigate = useNavigate();
  
  // Use the custom hook for all business logicC
  const {
    methods,
    onSubmit,
    watchedUsername,
    isLoading,
    rememberMe,
    showVerification,
    setShowVerification,
    verificationCode,
    errorMessage,
    setErrorMessage,
    handleCheckboxChange,
    handleVerificationChange,
    handleVerification,
    handleResendVerification,
    loadSavedCredentials,
    shouldRedirect
  } = useLoginForm();
  
  // UI-only state
  const [showEnvironmentSwitcher, setShowEnvironmentSwitcher] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showMobileAnimation, setShowMobileAnimation] = useState(true);

  // Artist slideshow data
  const imageData = [
    // { src: youngKingHenry, artist: "Young King Henry" },
    // { src: phoenixWonder, artist: "Phoenix Wonder" },
    // { src: sinaloNgcaba, artist: "Sinalo Ngcaba" },
    // { src: bloodyCivilian, artist: "Bloody Civilian" },
    { src: loginImg, artist: "" },
  ];

  // Handle initialization and navigation
  useEffect(() => {
    const initialize = async () => {
      // Check if user should be redirected
      if (shouldRedirect()) return;
      
      // Load saved credentials
      await loadSavedCredentials();
    };

    initialize();
  }, [shouldRedirect, loadSavedCredentials]);

  return (
    <main className="">
      <div className="login-page">
        <header className="fixed flex justify-between items-center top-0 left-0 right-0 z-50 p-8">
          {isNonProdEnvironment() && (
            <Button
              className="fixed top-8 right-8"
              onClick={() => setShowEnvironmentSwitcher(true)}>
              Environment
            </Button>
          )}
        </header>
        <section className="min-h-screen flex">
          {/* background  */}
          <div 
            className="fixed inset-0 bg-cover bg-center bg-no-repeat z-[-1] h-full"
            style={{
              backgroundImage: `url(${loginImg})`,
            }}
          >
             <div className="absolute inset-0 z-[1] bg-black/85" />
          </div>
         
          {/* Left side - hidden on mobile/tablet */}
          <div className="hidden lg:flex flex-[50%] grid place-items-center px-16 py-16">
            <div className="w-full h-full bg-white/5 rounded-[40px] p-12 overflow-hidden backdrop-blur-lg flex flex-col">
              {/* EIQ Logo */}
              <div className="mb-8">
                <img src={logo} alt="EIQ" className="w-16" />
              </div>
              
              {/* 3D Text Ring */}
              <div className="flex-1 w-full flex justify-center items-center">
                <TextRing3D
                  className="w-2/3 h-[calc(100%-10rem)]"
                />
              </div>
              
              {/* Hero Text */}
              <div className="space-y-6 mt-8">
                <h1 className="text-xl font-bold text-white leading-tight">
                Your AI Deal Orchestrator
                </h1>
                <p className="text-white/80 leading-relaxed max-w-lg">
                 Work with your team to evaluate, sign, and track deals with AI-powered clarity. From negotiation to payments, orchestrate everything in one secure platform.
                </p>
              </div>
            </div>
          </div>
          
          {/* Mobile Animation Overlay - only visible on mobile */}
          <div
            className="lg:hidden fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center"
            onClick={() => setShowMobileAnimation(false)}
            style={{
              opacity: showMobileAnimation ? 1 : 0,
              pointerEvents: showMobileAnimation ? 'auto' : 'none',
              transition: showMobileAnimation
                ? 'opacity 1.2s cubic-bezier(0.4, 0.0, 1, 1)'
                : 'opacity 1.2s cubic-bezier(0.4, 0.0, 1, 1), visibility 0s 1.2s',
              visibility: showMobileAnimation ? 'visible' : 'hidden',
            }}
          >
            <div className="w-[80vw] h-[80vw] max-w-md">
              <TextRing3D className="w-full h-full" />
            </div>
            <div
              className="absolute bottom-20 text-white/60 text-lg"
              style={{
                opacity: showMobileAnimation ? 1 : 0,
                transition: 'opacity 0.8s cubic-bezier(0.4, 0.0, 1, 1)'
              }}
            >
              Tap to continue
            </div>
          </div>

          {/* Right side - responsive */}
          <div className="w-full lg:flex-[50%] px-4 py-6 sm:px-8 lg:px-16 grid place-items-center">
            <div className="flex flex-col gap-2 w-full max-w-md lg:w-[90%]">
              <h4 className="text-lg font-medium text-white">
                Sign In 
              </h4>
              <p className="text-white/80 text-lg font-medium mb-6">Welcome to Equal IQ</p>
              {!showVerification ? (
                <FormProvider methods={methods} onSubmit={onSubmit}>
                  <div className="flex flex-col gap-4">
                    <div className="">
                      <label htmlFor="username" className="block text-white font-medium mb-2">Email</label> 
                      <div className="relative w-full h-full">
                        <div className="w-fit pointer-events-none absolute top-1/2 -translate-y-1/2 left-4 z-10">
                          <Email className="size-4" />
                        </div>
                        <RHFTextField
                          className="pl-8 text-foreground"
                          name="username"
                          type="email"
                          placeholder="Enter your email"
                        />
                      </div>
                    </div>
                    <div className="">
                      <label htmlFor="password" className="block text-white font-medium mb-2">Password</label>
                      <div className="relative w-full h-full">
                        <button aria-label="Toggle password visibility" className="w-fit absolute top-1/2 -translate-y-1/2 left-4" type="button" onClick={() => setShowPassword(!showPassword)}>
                          <Password className="size-4" />
                        </button>
                        <RHFTextField
                          className="pl-8 text-foreground"
                          name="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="*******"
                        />
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center text-white">
                        <Checkbox
                          checked={rememberMe}
                          id="rememberMe"
                          name="rememberMe"
                          onCheckedChange={handleCheckboxChange}
                        />
                        <label className="pl-2 text-base" htmlFor="rememberMe">Remember me</label>
                      </div>
                      <Link className="text-white/80 font-medium" to={PATH_AUTH.forgotPassword}>Forgot Password</Link>
                    </div>
                    <Button 
                      className={cn("rounded-full py-4", {"bg-white text-black": isLoading})} 
                      type="submit" 
                      disabled={isLoading}
                      variant="default"
                      size="lg"
                    >
                      {isLoading ? <SpinnerIcon className="size-8! text-black/60 animate-spin" /> : "Login"}
                    </Button>
                    <Button 
                      type="button" 
                      onClick={() => {
                        void navigateToDemoEntry(navigate);
                      }}
                      className="rounded-full text-white py-4"
                      variant="secondary"
                      size="lg"
                    >
                      Try Demo
                    </Button>
                    <p className="text-white mx-auto">
                      Don't have an account? <Link className="text-white/80" to={PATH_AUTH.signup}> Sign up</Link>
                    </p>
                  </div>
                </FormProvider>
              ) : (
                <form className="flex flex-col gap-4" onSubmit={handleVerification}>
                  <div className="space-y-4">
                    <h5 className="text-lg font-medium text-white">Verify Your Account</h5>
                    <p className="text-white/80">A verification code has been sent to <strong className="text-white">{watchedUsername}</strong>. Please check your email and enter the code below.</p>
                  </div>
                  
                  <div className="">
                    <label htmlFor="verificationCode" className="block text-white font-medium mb-2">Verification Code</label>
                    <input
                      className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white text-xl placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-transparent"
                      type="text"
                      name="verificationCode"
                      id="verificationCode"
                      placeholder="Enter verification code"
                      value={verificationCode}
                      onChange={handleVerificationChange}
                      autoFocus
                    />
                    {errorMessage && <span className="text-red-400 text-sm mt-2 block">{errorMessage}</span>}
                  </div>
                  
                  <div className="flex flex-col gap-4">
                    <Button 
                      className={cn("rounded-full text-lg h-auto w-full py-4 font-medium disabled:bg-white/25 disabled:opacity-100", {"bg-white text-black": isLoading})} 
                      type="submit" 
                      disabled={isLoading}
                      variant="default"
                      size="lg"
                    >
                      {isLoading ? <SpinnerIcon className="!w-8 !h-8 text-black/60 animate-spin" /> : "Verify Account"}
                    </Button>
                    
                    <Button
                      className="rounded-full text-lg h-auto w-full py-8 font-medium bg-transparent border border-white/30 text-white hover:bg-white/10"
                      type="button"
                      onClick={handleResendVerification}
                      disabled={isLoading}
                      variant="outline"
                      size="lg"
                    >
                      Resend Code
                    </Button>
                  </div>
                  
                  <p className="text-white mx-auto">
                    <Link
                      className="text-white/80"
                      to="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowVerification(false);
                        setErrorMessage("");
                      }}
                    >
                      Back to Login
                    </Link>
                  </p>
                </form>
              )}
              </div>
          </div>
        </section>
      </div>
      
      <EnvironmentSwitcher 
        open={showEnvironmentSwitcher} 
        onClose={() => setShowEnvironmentSwitcher(false)} 
      />
    </main>
  );
};

export default Login;